//# sourceMappingURL=month.mjs.map
/// <reference types="./month.d.mts" />
import * as $bool from "../../../../gleam_stdlib/gleam/bool.mjs";
import * as $int from "../../../../gleam_stdlib/gleam/int.mjs";
import * as $result from "../../../../gleam_stdlib/gleam/result.mjs";
import * as $calendar from "../../../../gleam_time/gleam/time/calendar.mjs";
import { Ok, makeError } from "../../../gleam.mjs";

const FILEPATH = "src/gleam/time/calendar/month.gleam";

/**
 * Convert a month to string, with the full month's name.
 *
 * ```gleam
 * to_string(calendar.January)   // -> "January"
 * to_string(calendar.February)  // -> "February"
 * to_string(calendar.March)     // -> "March"
 * to_string(calendar.April)     // -> "April"
 * to_string(calendar.May)       // -> "May"
 * to_string(calendar.June)      // -> "June"
 * to_string(calendar.July)      // -> "July"
 * to_string(calendar.August)    // -> "August"
 * to_string(calendar.September) // -> "September"
 * to_string(calendar.October)   // -> "October"
 * to_string(calendar.November)  // -> "November"
 * to_string(calendar.December)  // -> "December"
 * ```
 */
export function to_string(month) {
  if (month instanceof $calendar.January) {
    return "January";
  } else if (month instanceof $calendar.February) {
    return "February";
  } else if (month instanceof $calendar.March) {
    return "March";
  } else if (month instanceof $calendar.April) {
    return "April";
  } else if (month instanceof $calendar.May) {
    return "May";
  } else if (month instanceof $calendar.June) {
    return "June";
  } else if (month instanceof $calendar.July) {
    return "July";
  } else if (month instanceof $calendar.August) {
    return "August";
  } else if (month instanceof $calendar.September) {
    return "September";
  } else if (month instanceof $calendar.October) {
    return "October";
  } else if (month instanceof $calendar.November) {
    return "November";
  } else {
    return "December";
  }
}

/**
 * Convert a month to short string.
 *
 * ```gleam
 * to_short_string(calendar.January)   // -> "Jan"
 * to_short_string(calendar.February)  // -> "Feb"
 * to_short_string(calendar.March)     // -> "Mar"
 * to_short_string(calendar.April)     // -> "Apr"
 * to_short_string(calendar.May)       // -> "May"
 * to_short_string(calendar.June)      // -> "Jun"
 * to_short_string(calendar.July)      // -> "Jul"
 * to_short_string(calendar.August)    // -> "Aug"
 * to_short_string(calendar.September) // -> "Sep"
 * to_short_string(calendar.October)   // -> "Oct"
 * to_short_string(calendar.November)  // -> "Nov"
 * to_short_string(calendar.December)  // -> "Dec"
 * ```
 */
export function to_short_string(month) {
  if (month instanceof $calendar.January) {
    return "Jan";
  } else if (month instanceof $calendar.February) {
    return "Feb";
  } else if (month instanceof $calendar.March) {
    return "Mar";
  } else if (month instanceof $calendar.April) {
    return "Apr";
  } else if (month instanceof $calendar.May) {
    return "May";
  } else if (month instanceof $calendar.June) {
    return "Jun";
  } else if (month instanceof $calendar.July) {
    return "Jul";
  } else if (month instanceof $calendar.August) {
    return "Aug";
  } else if (month instanceof $calendar.September) {
    return "Sep";
  } else if (month instanceof $calendar.October) {
    return "Oct";
  } else if (month instanceof $calendar.November) {
    return "Nov";
  } else {
    return "Dec";
  }
}

/**
 * Convert a month to integer.
 *
 * ```gleam
 * to_int(calendar.January)   // -> 1
 * to_int(calendar.February)  // -> 2
 * to_int(calendar.March)     // -> 3
 * to_int(calendar.April)     // -> 4
 * to_int(calendar.May)       // -> 5
 * to_int(calendar.June)      // -> 6
 * to_int(calendar.July)      // -> 7
 * to_int(calendar.August)    // -> 8
 * to_int(calendar.September) // -> 9
 * to_int(calendar.October)   // -> 10
 * to_int(calendar.November)  // -> 11
 * to_int(calendar.December)  // -> 12
 * ```
 */
export function to_int(month) {
  return $calendar.month_to_int(month);
}

/**
 * Convert a month coming from `<input type="date">` to month.
 *
 * ```gleam
 * from_input("1")  // -> Ok(calendar.January)
 * from_input("01")  // -> Ok(calendar.January)
 * from_input("00001")  // -> Ok(calendar.January)
 * from_input("2")  // -> Ok(calendar.February)
 * from_input("3")  // -> Ok(calendar.March)
 * from_input("4")  // -> Ok(calendar.April)
 * from_input("5")  // -> Ok(calendar.May)
 * from_input("6")  // -> Ok(calendar.June)
 * from_input("7")  // -> Ok(calendar.July)
 * from_input("8")  // -> Ok(calendar.August)
 * from_input("9")  // -> Ok(calendar.September)
 * from_input("10") // -> Ok(calendar.October)
 * from_input("11") // -> Ok(calendar.November)
 * from_input("12") // -> Ok(calendar.December)
 * from_input(_)    // -> Error(Nil)
 * ```
 */
export function from_input(month) {
  let _pipe = $int.parse(month);
  return $result.try$(_pipe, $calendar.month_from_int);
}

export function add(month, count) {
  return $bool.guard(
    count < 1,
    month,
    () => {
      let month$1 = to_int(month);
      let month$2 = (month$1 % 12) + 1;
      let $ = $calendar.month_from_int(month$2);
      let month$3;
      if ($ instanceof Ok) {
        month$3 = $[0];
      } else {
        throw makeError(
          "let_assert",
          FILEPATH,
          "gleam/time/calendar/month",
          120,
          "add",
          "Pattern match failed, no pattern matched the value.",
          {
            value: $,
            start: 3989,
            end: 4042,
            pattern_start: 4000,
            pattern_end: 4009
          }
        )
      }
      return add(month$3, count - 1);
    },
  );
}

export function days(month, year) {
  let is_leap = $calendar.is_leap_year(year);
  if (month instanceof $calendar.January) {
    return 31;
  } else if (month instanceof $calendar.February) {
    if (is_leap) {
      return 29;
    } else {
      return 28;
    }
  } else if (month instanceof $calendar.March) {
    return 31;
  } else if (month instanceof $calendar.April) {
    return 30;
  } else if (month instanceof $calendar.May) {
    return 31;
  } else if (month instanceof $calendar.June) {
    return 30;
  } else if (month instanceof $calendar.July) {
    return 31;
  } else if (month instanceof $calendar.August) {
    return 31;
  } else if (month instanceof $calendar.September) {
    return 30;
  } else if (month instanceof $calendar.October) {
    return 31;
  } else if (month instanceof $calendar.November) {
    return 30;
  } else {
    return 31;
  }
}
